import { DataTable } from "@/components/Base/Datatable";
import { useEffect, useState } from "react";
import { Skeleton } from "@/components/Base/Skeleton";
import { useSearchParams } from "react-router-dom";
import { PaginationState, SortingState } from "@tanstack/react-table";
import { userColumns } from "@/components/Users/Users.columns";
import { usePaginatedUsers } from "@/hooks/queries/usePaginatedUsers";
import { useTotalUsersQuery } from "@/hooks/queries/useTotalUsersQuery";
import { Button } from "@/components/Base/Button";
import { IconNames, User } from "@/types/v2";
import { UserDetailsDialog } from "@/components/Users/UserDetailsDialog";
import { Icon } from "@/components/Common/Icon";
import { Input } from "@/components/Base/Input";

export const UsersTable = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [searchInput, setSearchInput] = useState(
    searchParams.get("search") || ""
  );

  // Get values from URL params
  const currentPage = parseInt(searchParams.get("page") || "1", 10);
  const pageSize = parseInt(searchParams.get("pageSize") || "10", 10);
  const sortBy = searchParams.get("sortBy") ?? "createdAt"; // Default sort by createdAt
  const sortOrder = (searchParams.get("sortOrder") as "asc" | "desc") ?? "desc"; // Default sort order is descending
  const searchTerm = searchParams.get("search") || "";

  // Debounce search input
  const [debouncedSearch, setDebouncedSearch] = useState(searchTerm);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchInput);

      // Update URL with search term
      const newParams = new URLSearchParams(searchParams);
      if (searchInput.trim()) {
        newParams.set("search", searchInput.trim());
        newParams.set("page", "1"); // Reset to page 1 on search
      } else {
        newParams.delete("search");
      }
      setSearchParams(newParams);
    }, 300); // 300ms debounce

    return () => clearTimeout(timer);
  }, [searchInput]);

  const { data, isLoading } = usePaginatedUsers({
    currentPage,
    pageSize,
    sortBy,
    sortOrder,
    searchTerm: debouncedSearch,
  });

  const { data: totalUsers } = useTotalUsersQuery();

  // Helper function to get sorting from params
  const getSortingFromParams = (): SortingState => {
    if (sortBy && sortOrder) {
      return [{ id: sortBy, desc: sortOrder === "desc" }];
    }
    return [];
  };

  // Initialize state from URL params
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: currentPage - 1,
    pageSize: pageSize,
  });

  const [sorting, setSorting] = useState<SortingState>(getSortingFromParams());

  // Update pagination state when URL changes (browser back/forward)
  useEffect(() => {
    const newPageIndex = currentPage - 1;
    const newPageSize = pageSize;

    // Only update if values actually changed
    if (
      pagination.pageIndex !== newPageIndex ||
      pagination.pageSize !== newPageSize
    ) {
      setPagination({
        pageIndex: newPageIndex,
        pageSize: newPageSize,
      });
    }
  }, [currentPage, pageSize]); // Remove pagination from dependencies

  // Update sorting state when URL changes
  useEffect(() => {
    const newSorting = getSortingFromParams();

    // Only update if sorting actually changed
    const sortingChanged =
      sorting.length !== newSorting.length ||
      (newSorting.length > 0 &&
        (sorting.length === 0 ||
          sorting[0].id !== newSorting[0].id ||
          sorting[0].desc !== newSorting[0].desc));

    if (sortingChanged) {
      setSorting(newSorting);
    }
  }, [sortBy, sortOrder]); // Use specific params instead of searchParams

  // Update URL when pagination changes (but only from user interaction)
  useEffect(() => {
    const urlPage = currentPage;
    const urlPageSize = parseInt(searchParams.get("pageSize") || "10", 10);
    const actualPage = pagination.pageIndex + 1;

    // Only update URL if pagination state differs from URL state
    if (actualPage !== urlPage || pagination.pageSize !== urlPageSize) {
      const newParams = new URLSearchParams(searchParams);
      newParams.set("page", actualPage.toString());
      newParams.set("pageSize", pagination.pageSize.toString());
      setSearchParams(newParams);
    }
  }, [pagination.pageIndex, pagination.pageSize]); // Remove searchParams from dependencies

  // Handle sorting changes and reset pagination
  const handleSortingChange = (
    updaterOrValue: SortingState | ((old: SortingState) => SortingState)
  ) => {
    const newSorting =
      typeof updaterOrValue === "function"
        ? updaterOrValue(sorting)
        : updaterOrValue;

    setSorting(newSorting);

    // Update URL with new sorting and reset to page 1
    const newParams = new URLSearchParams(searchParams);

    if (newSorting.length > 0) {
      const sort = newSorting[0];
      newParams.set("sortBy", sort.id);
      newParams.set("sortOrder", sort.desc ? "desc" : "asc");
    } else {
      newParams.delete("sortBy");
      newParams.delete("sortOrder");
    }

    // Reset to page 1 when sorting changes
    newParams.set("page", "1");
    setSearchParams(newParams);

    // Update local pagination state immediately to prevent flicker
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  };

  const handleViewDetails = (user: User) => {
    setSelectedUser(user);
    setIsDialogOpen(true);
  };

  const handleDialogClose = (open: boolean) => {
    setIsDialogOpen(open);
    if (!open) {
      setSelectedUser(null);
    }
  };

  const handleClearSearch = () => {
    setSearchInput("");
    const newParams = new URLSearchParams(searchParams);
    newParams.delete("search");
    newParams.set("page", "1");
    setSearchParams(newParams);
  };

  if (!data || isLoading) {
    return (
      <div className="flex flex-col gap-4">
        <Skeleton className="h-12 w-24 self-end" />
        <Skeleton className="h-24 w-full" />
        <div className="flex flex-row justify-end gap-4">
          <Skeleton className="h-12 w-24" />
          <Skeleton className="h-12 w-24" />
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="flex flex-col gap-4">
        {/* Search Input */}
        <div className="flex items-center gap-2">
          <Input
            type="text"
            placeholder="Search by email (case sensitive)"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className="w-64"
          />
          {searchInput && (
            <Button variant="outline" onClick={handleClearSearch}>
              Clear
            </Button>
          )}
        </div>

        <DataTable
          // Data is based on the data list, with pagination applied
          data={data?.docs}
          columns={userColumns({
            actions: (user) => {
              return [
                <Button
                  key="view"
                  variant="ghost-alt"
                  size="sm"
                  onClick={() => handleViewDetails(user)}
                >
                  <Icon name={IconNames.open} className="mr-2 h-4 w-4" />
                  View Details
                </Button>,
              ];
            },
          })}
          pagination={pagination}
          setPagination={setPagination}
          sorting={sorting}
          setSorting={handleSortingChange}
          rowCount={totalUsers}
          loading={isLoading}
          userDefaults={{
            columnVisibility: {
              // Show the ID column by default
              id: true,
            },
          }}
        />

        <UserDetailsDialog
          user={selectedUser}
          open={isDialogOpen}
          onOpenChange={handleDialogClose}
        />
      </div>
    </>
  );
};
